import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { KpiCard } from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, GlassWater, IndianRupee, AlertTriangle, Target, MapPin, Package, Wrench, Receipt,
} from "lucide-react";
import { formatINR, formatNum, formatLakhs } from "@/lib/format";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";

const RED = "#E11D2A"; const GRAY = "#9CA3AF";

export default function StoreDetail() {
  const { storeId } = useParams();
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stores/${storeId}/detail?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [storeId, days]); // eslint-disable-line

  if (loading || !data) return <div className="p-8 text-center text-gray-500">Loading store detail…</div>;

  const { store, kpis, timeline, consumables, machine_logs, issues, purchases, expenses } = data;
  const dailyStock = {};
  consumables.forEach(c => {
    dailyStock[c.date] = dailyStock[c.date] || { date: c.date };
    dailyStock[c.date][c.item] = c.closing_stock;
  });
  const stockData = Object.values(dailyStock).sort((a,b) => a.date.localeCompare(b.date));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title={store.store_name} subtitle={`${store.store_code} · ${store.city}`} />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button data-testid="back-to-dashboard-btn" variant="outline" onClick={() => nav(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-4 w-4 text-brand-red" />
              {store.address}
            </div>
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger data-testid="detail-days-select" className="w-40 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger-in">
            <KpiCard testId="detail-kpi-cups" label="Cups" value={formatNum(kpis.total_glasses)} icon={GlassWater} accent="bg-red-50 text-brand-red" sub={`${kpis.days_active} active days`} />
            <KpiCard testId="detail-kpi-sales" label="Revenue" value={formatLakhs(kpis.total_sales)} icon={IndianRupee} accent="bg-red-50 text-brand-red" />
            <KpiCard testId="detail-kpi-target" label="Target %" value={`${kpis.target_pct}%`} icon={Target}
              accent={kpis.target_pct>=80?"bg-green-50 text-green-700":kpis.target_pct>=50?"bg-amber-50 text-amber-700":"bg-red-50 text-brand-red"} />
            <KpiCard testId="detail-kpi-damage" label="Shrinkage" value={formatNum(kpis.total_damage)} icon={AlertTriangle} accent="bg-amber-50 text-amber-700" sub="cups" />
          </div>

          <Card className="p-5 rounded-xl border-gray-200 bg-white">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Daily sales trend</div>
            <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Cups per day</div>
            <div className="mt-4 h-64" data-testid="detail-trend-chart">
              <ResponsiveContainer>
                <LineChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Line type="monotone" dataKey="glasses" stroke={RED} strokeWidth={2.5} dot={{ r: 3 }} name="Cups" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {stockData.length > 0 && (
            <Card className="p-5 rounded-xl border-gray-200 bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Closing stock history</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">BIB / CO2 / Water</div>
              <div className="mt-4 h-56" data-testid="detail-stock-chart">
                <ResponsiveContainer>
                  <BarChart data={stockData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                    <Bar dataKey="BIB" fill={RED} radius={[4,4,0,0]} />
                    <Bar dataKey="CO2" fill="#0A0A0A" radius={[4,4,0,0]} />
                    <Bar dataKey="Water" fill="#3B82F6" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5 rounded-xl border-gray-200 bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Issues</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">{issues.length} raised</div>
              <div className="mt-4 space-y-2 max-h-64 overflow-y-auto" data-testid="detail-issues-list">
                {issues.length === 0 && <div className="text-sm text-gray-500 py-4 text-center">No issues in the last {days} days</div>}
                {issues.map(i => (
                  <div key={i.id} className="flex items-center gap-2 p-2 border border-gray-100 rounded-lg">
                    <Wrench className="h-4 w-4 text-brand-red" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{i.issue_type}</div>
                      <div className="text-xs text-gray-500 truncate">{i.details}</div>
                    </div>
                    <Badge variant="outline">{i.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5 rounded-xl border-gray-200 bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Machine downtime</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">{machine_logs.filter(m => m.machine_status === "Down").length} events</div>
              <div className="mt-4 space-y-2 max-h-64 overflow-y-auto" data-testid="detail-machine-list">
                {machine_logs.length === 0 && <div className="text-sm text-gray-500 py-4 text-center">No machine logs</div>}
                {machine_logs.map(m => (
                  <div key={m.id} className="flex items-center gap-2 p-2 border border-gray-100 rounded-lg">
                    <Package className={`h-4 w-4 ${m.machine_status === "Down" ? "text-red-600" : "text-green-600"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{m.machine_status} · {m.issue_type}</div>
                      <div className="text-xs text-gray-500">{m.date} · {m.downtime_hours}h · {m.reported_by}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {expenses && (
            <Card className="p-5 rounded-xl border-gray-200 bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Recent expenses & purchases</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3" data-testid="detail-finance">
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Purchases (BIB/CO2)</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {purchases.map(p => (
                      <div key={p.id} className="flex justify-between text-xs p-2 rounded border border-gray-100">
                        <span>{p.date} · {p.item} × {p.quantity}</span>
                        <span className="font-semibold">{formatINR(p.amount)}</span>
                      </div>
                    ))}
                    {purchases.length === 0 && <div className="text-xs text-gray-400 text-center py-4">None</div>}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Expenses</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {expenses.map(e => (
                      <div key={e.id} className="flex justify-between text-xs p-2 rounded border border-gray-100">
                        <span>{e.date} · {e.category}</span>
                        <span className="font-semibold">{formatINR(e.amount)}</span>
                      </div>
                    ))}
                    {expenses.length === 0 && <div className="text-xs text-gray-400 text-center py-4">None</div>}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}
